import { useContext, useEffect, useRef } from 'react';
import { useKeystoneSession } from '../auth/use-keystone-session';

import { AppContext, DesignState } from '@context';
import { getCanonicalDesignId } from '@helpers';
import { saveRecent } from '@external';
import { customerState, selectNavigation, useAppSelector } from '@state';

const resolveDesignLabel = (design: DesignState, fallback: string): string => {
	try {
		const { blueprint, bpController } = design;
		const kitchen = bpController?.kitchen ?? blueprint.kitchens.getActiveVersion() ?? null;
		return kitchen?.description?.trim() || fallback;
	} catch (error) {
		console.warn('[use-record-recent-design] could not read the design description', error);
		return fallback;
	}
};

/**
 * Mirrors the local `recent-customers` list to the API so the designer portal can offer back what
 * this designer opened, on any machine. Best effort -- a failure here must never block opening a design.
 */
export const useRecordRecentDesign = (): void => {
	const { getToken, status } = useKeystoneSession();
	const { appState: { customerDesigns: designs } } = useContext(AppContext);
	const appNav = useAppSelector(selectNavigation);
	const { firstName, lastName } = useAppSelector(customerState);
	const lastRecordedId = useRef<string | null>(null);

	useEffect(() => {
		if (status !== 'ready' || !appNav?.email || !appNav?.designId) return;
		if (designs.isLoading || !designs.hasLoaded) return;

		const design = designs.designs.find((entry) => entry.blueprint.id === appNav.designId) ?? null;
		if (design === null) return;

		const id = getCanonicalDesignId(design.blueprint);
		if (!id || lastRecordedId.current === id) return;
		lastRecordedId.current = id;

		const customerName = [firstName, lastName].filter(Boolean).join(' ').trim();
		const label = resolveDesignLabel(design, customerName || appNav.email);
		saveRecent({ kind: 'design', id, customerEmail: appNav.email, label, getToken })
			.then(([, error]) => {
				if (error) console.warn('[use-record-recent-design] not recorded', id, error);
			});
	}, [status, appNav?.email, appNav?.designId, designs.isLoading, designs.hasLoaded, designs.designs.length]);
};
